import type { EmailChannelView, EmailProvider, GmailSenderSettings, SmtpTlsMode } from "../api/types";

/**
 * Everything the email channel form knows that is not a React component.
 *
 * Split out from `EmailChannelForm.tsx` so it can be unit-tested without a DOM or a
 * renderer: this file decides whether a save is allowed, what gets sent, and what
 * the console claims about a Gmail connection, and all three are worth a test that
 * runs in a second. The `.tsx` beside it is the rendering.
 *
 * The validation here deliberately mirrors the backend (`deliver/email.ts`,
 * `deliver/addresses.ts`, `deliver/gmail.ts`). The server re-checks all of it —
 * this is a courtesy that turns a round trip into an inline message, not the
 * boundary.
 */

export const EMAIL_PROVIDERS: readonly EmailProvider[] = ["smtp", "gmail_oauth"];

/**
 * Mirrors `SMTP_TLS_MODES` in the backend's deliver/smtp.ts. Duplicated rather than
 * fetched because `/api/meta` does not publish it: it is the one enum this console
 * has to know that the API never sends, and a wrong value here is a 400, not a
 * silent misconfiguration.
 */
export const SMTP_TLS_MODES: readonly SmtpTlsMode[] = ["starttls", "tls", "none"];

/** Default submission port per TLS mode, mirroring `DEFAULT_PORTS` in deliver/email.ts. */
export const DEFAULT_PORTS: Record<SmtpTlsMode, number> = { starttls: 587, tls: 465, none: 25 };

/** Bare `local@domain`, the shape `deliver/addresses.ts` enforces before a header. */
const ADDRESS = /^[^\s<>@,;:"()[\]\\]+@[^\s<>@,;:"()[\]\\]+$/;
/** Hostname, IPv4, or bracketed IPv6 — anything that cannot break a command line. */
const HOST = /^[A-Za-z0-9._-]+$|^\[[0-9A-Fa-f:.]+\]$/;

export const MAX_RECIPIENTS = 20;
export const MAX_ADDRESS_CHARS = 320;
export const MAX_FROM_NAME_CHARS = 120;
const MAX_HOST_CHARS = 253;

/**
 * Read an email channel's `extra` into a typed view.
 *
 * `extra` is `Record<string, unknown>` because it carries whatever the channel type
 * needs; for `email` it is the provider's config minus every secret-named key,
 * which the API strips on read. Anything missing or of the wrong type reads as
 * unset, so a row hand-edited in SQL shows up as an incomplete channel rather than
 * as a crash.
 *
 * A config with no `provider` is SMTP — that is every channel stored before Gmail
 * existed, and they are still valid.
 */
export function readEmailChannel(extra: Record<string, unknown>): EmailChannelView {
  const port = extra["port"];
  const tls = extra["tls"];
  const to = extra["to"];
  const provider = extra["provider"];
  return {
    provider: provider === "gmail_oauth" ? "gmail_oauth" : "smtp",
    host: text(extra["host"]),
    port: typeof port === "number" && Number.isFinite(port) ? port : null,
    tls: SMTP_TLS_MODES.find((mode) => mode === tls) ?? null,
    from: text(extra["from"]),
    fromName: text(extra["fromName"]),
    to: Array.isArray(to) ? to.filter((item): item is string => typeof item === "string") : [],
    // Only a row that predates the global sender still carries this. It is read so
    // the console can name the leftover, never so anything sends from it.
    legacyAccount: text(extra["connectedEmail"]),
  };
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/* ------------------------------------------------------------------- smtp -- */

export interface EmailFormState {
  provider: EmailProvider;
  host: string;
  port: string;
  tls: SmtpTlsMode;
  from: string;
  fromName: string;
  /** Free text: the operator may separate recipients with commas, spaces or newlines. */
  to: string;
  username: string;
  password: string;
}

/** A blank form, or one seeded from the non-secret half the API returned. */
export function emailFormState(view?: EmailChannelView): EmailFormState {
  return {
    provider: view?.provider ?? "smtp",
    host: view?.host ?? "",
    port: view?.port === null || view?.port === undefined ? "" : String(view.port),
    tls: view?.tls ?? "starttls",
    from: view?.from ?? "",
    fromName: view?.fromName ?? "",
    to: (view?.to ?? []).join(", "),
    // Never seeded: the API does not return either, by design.
    username: "",
    password: "",
  };
}

export type EmailFormField = "host" | "port" | "tls" | "from" | "fromName" | "to" | "auth";

export interface EmailFormCheck {
  /** Parsed, trimmed, de-duplicated recipient list. */
  recipients: string[];
  /** Per-field messages that block the save button. */
  errors: Partial<Record<EmailFormField, string>>;
  /**
   * Things the server may still reject but this form cannot be sure about — chiefly
   * anything that depends on the stored credential it was never shown.
   */
  warnings: string[];
  ok: boolean;
}

/**
 * Split what the operator typed into addresses.
 *
 * Commas, semicolons and whitespace all count, because all three are what a
 * pasted distribution list actually looks like. Duplicates are dropped
 * case-insensitively: some relays treat a repeated RCPT TO as a second delivery.
 */
export function parseRecipients(text_: string): string[] {
  const out: string[] = [];
  for (const raw of text_.split(/[\s,;]+/)) {
    const address = raw.trim();
    if (address === "") continue;
    if (!out.some((existing) => existing.toLowerCase() === address.toLowerCase())) out.push(address);
  }
  return out;
}

/** The recipient rules both providers share. */
function checkRecipients(typed: string): { recipients: string[]; error?: string } {
  const recipients = parseRecipients(typed);
  const bad = recipients.find((address) => !ADDRESS.test(address) || address.length > MAX_ADDRESS_CHARS);
  if (recipients.length === 0) return { recipients, error: "At least one recipient is required." };
  if (bad !== undefined) return { recipients, error: `"${bad.slice(0, 60)}" is not a usable email address.` };
  if (recipients.length > MAX_RECIPIENTS) {
    return {
      recipients,
      error: `At most ${MAX_RECIPIENTS} recipients — this is one message, not a mailing list.`,
    };
  }
  return { recipients };
}

/**
 * Check the form the way the server will.
 *
 * `mode` matters only for the credential pair. On create this form knows the whole
 * truth — a lone username is a config the server will refuse, so block it. On edit
 * it does not: a typed password with a blank username is a plausible rotation
 * against a stored username, so it warns and lets the server have the last word.
 *
 * A Gmail form is checked on its recipients alone. There is no sender, host, port
 * or credential to get wrong, because the connected account supplies all four.
 */
export function validateEmailForm(state: EmailFormState, mode: "create" | "edit"): EmailFormCheck {
  const errors: Partial<Record<EmailFormField, string>> = {};
  const warnings: string[] = [];

  const { recipients, error: recipientError } = checkRecipients(state.to);
  if (recipientError !== undefined) errors.to = recipientError;

  if (state.provider === "gmail_oauth") {
    return { recipients, errors, warnings, ok: Object.keys(errors).length === 0 };
  }

  const host = state.host.trim();
  if (host === "") errors.host = "Required — the SMTP relay to connect to.";
  else if (!HOST.test(host) || host.length > MAX_HOST_CHARS) {
    errors.host = "Must be a hostname or IP address, e.g. smtp.gmail.com or [::1].";
  }

  const port = state.port.trim();
  if (port !== "") {
    const parsed = Number(port);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 65_535) {
      errors.port = "Must be a whole number between 1 and 65535.";
    }
  }

  const from = state.from.trim();
  if (from === "") errors.from = "Required — the envelope sender the relay will accept.";
  else if (!ADDRESS.test(from) || from.length > MAX_ADDRESS_CHARS) {
    errors.from = "Must be a single address, e.g. leads@yourdomain.com.";
  }

  if (state.fromName.trim().length > MAX_FROM_NAME_CHARS) {
    errors.fromName = `At most ${MAX_FROM_NAME_CHARS} characters.`;
  }

  const username = state.username.trim();
  const password = state.password;
  const typedOne = (username === "") !== (password === "");
  if (typedOne) {
    if (mode === "create") {
      errors.auth = "Set the username and password together, or leave both blank for an open relay.";
    } else {
      warnings.push(
        "Only one half of the credential was typed. The other one is kept only if it is already " +
          "stored — otherwise the save is rejected.",
      );
    }
  }

  // AUTH over a cleartext connection puts the password on the wire in base64. The
  // server exempts loopback so a developer can point at a local capture relay.
  const loopback = host === "localhost" || host === "127.0.0.1" || host === "[::1]";
  if (state.tls === "none" && !loopback) {
    if (username !== "" || password !== "") {
      errors.auth = `TLS "none" would send the SMTP password in the clear. Use STARTTLS or TLS.`;
    } else if (mode === "edit") {
      warnings.push(
        `TLS "none" is rejected for a relay that has a stored credential, because the password ` +
          "would go out in the clear.",
      );
    }
  }

  return { recipients, errors, warnings, ok: Object.keys(errors).length === 0 };
}

/**
 * The `extra` object to send.
 *
 * For SMTP, a blank credential field is omitted rather than sent as an empty
 * string: the API reads an absent secret as "keep the stored one", which is the only
 * reason an operator can edit the recipient list without re-typing the SMTP
 * password they were never shown. A blank sender name *is* sent as null, so it can
 * be cleared.
 *
 * For Gmail there is nothing else to send. The sending account belongs to the
 * deployment, not to this channel, so a channel write has no opinion about it — and
 * a legacy row's leftover `connectedEmail` is deliberately not echoed back, which is
 * how saving such a channel cleans it up.
 */
export function emailChannelExtra(state: EmailFormState, recipients: string[]): Record<string, unknown> {
  if (state.provider === "gmail_oauth") {
    return { provider: "gmail_oauth", to: recipients };
  }
  const port = state.port.trim();
  const fromName = state.fromName.trim();
  const username = state.username.trim();
  return {
    provider: "smtp",
    host: state.host.trim(),
    port: port === "" ? null : Number(port),
    tls: state.tls,
    from: state.from.trim(),
    fromName: fromName === "" ? null : fromName,
    to: recipients,
    ...(username === "" ? {} : { username }),
    ...(state.password === "" ? {} : { password: state.password }),
  };
}

/* ------------------------------------------------------------------ gmail -- */

/**
 * What the console is allowed to say about the deployment's Gmail sender.
 *
 * One connection for the whole installation, so this is a fact about the server and
 * not about whichever channel happens to be on screen. `connected` comes from the
 * API's own verdict and is never inferred here: the encrypted refresh token is not
 * disclosed to the browser at all, and guessing from anything else would let a
 * console claim delivery works when nobody can name the mailbox it would come from.
 */
export type GmailSenderState =
  | { kind: "unavailable"; headline: string; detail: string }
  | { kind: "disconnected"; headline: string; detail: string }
  | { kind: "pending"; headline: string; detail: string }
  | { kind: "connected"; headline: string; detail: string; account: string };

/**
 * `undefined` for settings that have not loaded yet, which reads as "not connected"
 * without claiming the server said so — the panels that use this render a neutral
 * message rather than an alarming one while the request is in flight.
 */
export function gmailSenderState(settings: GmailSenderSettings | undefined): GmailSenderState {
  if (settings === undefined) {
    return {
      kind: "disconnected",
      headline: "Checking the email sender…",
      detail: "Reading this server's Google connection.",
    };
  }
  if (!settings.capability.enabled) {
    return {
      kind: "unavailable",
      headline: "Gmail is not enabled on this server",
      detail:
        settings.capability.problem ??
        "The deployment has no Google OAuth client configured, so no account can be connected. " +
          "Set GOOGLE_OAUTH_CLIENT_ID, GOOGLE_OAUTH_CLIENT_SECRET, GOOGLE_OAUTH_REDIRECT_URI, " +
          "ADMIN_CONSOLE_URL and GOOGLE_OAUTH_TOKEN_ENCRYPTION_KEY, or use SMTP relays instead.",
    };
  }
  const { connection } = settings;
  if (connection.connected && connection.account !== null) {
    return {
      kind: "connected",
      account: connection.account,
      headline: `Sending as ${connection.account}`,
      detail:
        "Every Gmail delivery channel mails from this account through the Gmail API. Reconnect if " +
        "the account's password changed or access was revoked; disconnecting stops Gmail delivery " +
        "for every client at once.",
    };
  }
  if (connection.pending) {
    return {
      kind: "pending",
      headline: "Waiting on Google's consent screen",
      detail:
        "A connection was started and has not come back yet. Finish it in the tab that opened, or " +
        "start again — the link expires ten minutes after it was created.",
    };
  }
  return {
    kind: "disconnected",
    headline: "No Google account connected",
    detail:
      "No Gmail channel can deliver until an owner connects the account this server sends from. " +
      "SMTP channels are unaffected.",
  };
}

/** What a channel form says about the sender it will use. One line, plus a warning. */
export function gmailSenderNote(state: GmailSenderState): string {
  switch (state.kind) {
    case "connected":
      return `Leads go out as ${state.account}. Recipients below are who receives them.`;
    case "pending":
      return "The sender account is being connected. Recipients can be saved now.";
    case "unavailable":
      return "Gmail is switched off on this server, so this channel cannot deliver.";
    default:
      return "No sender account is connected yet, so this channel will be saved inactive.";
  }
}

/** The five words the OAuth callback can send the console back with. */
export const GMAIL_RETURN_STATUSES = ["connected", "denied", "failed", "expired", "reconsent"] as const;
export type GmailReturnStatus = (typeof GMAIL_RETURN_STATUSES)[number];

export interface GmailReturnMessage {
  severity: "success" | "warning" | "error";
  message: string;
}

/**
 * Turn `?gmail=…` into something to show.
 *
 * Only these five words are recognised; anything else in the query string is
 * ignored rather than rendered, because the parameter arrives from a redirect and
 * is not a place to trust free text.
 */
export function gmailReturnMessage(value: string | null): GmailReturnMessage | null {
  switch (value) {
    case "connected":
      return {
        severity: "success",
        message: "Google account connected. Every Gmail delivery channel now sends from it.",
      };
    case "denied":
      return {
        severity: "warning",
        message: "The Google account did not grant access, so nothing was connected.",
      };
    case "reconsent":
      return {
        severity: "warning",
        message:
          "Google did not return everything this needs — the “Send email on your behalf” permission " +
          "has to stay ticked. Try connecting again and accept all requested permissions.",
      };
    case "expired":
      return {
        severity: "warning",
        message: "That connection link had already been used or had expired. Start the connection again.",
      };
    case "failed":
      return {
        severity: "error",
        message: "Connecting the Google account failed. Try again; the audit log records the reason.",
      };
    default:
      return null;
  }
}
